import { useRef } from 'react'
import { Box } from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { ICON_LG } from '../common/theme'

interface ResizableDividerProps {
  // Called for every mouse-move during a drag with the delta in pixels since
  // the previous call. Consumer typically adds the delta to its width state.
  onDelta: (dx: number) => void
}

// 7px-wide vertical drag handle used between LHS / RHS panels in template
// management screens. While dragging, attaches global mousemove/mouseup
// listeners and forces a col-resize cursor + disables text selection so the
// drag feels native.
export default function ResizableDivider({ onDelta }: ResizableDividerProps) {
  const lastXRef = useRef(0)

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    lastXRef.current = e.clientX

    const onMove = (ev: MouseEvent) => {
      const dx = ev.clientX - lastXRef.current
      lastXRef.current = ev.clientX
      if (dx !== 0) onDelta(dx)
    }
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }

    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }

  return (
    <Box
      onMouseDown={handleMouseDown}
      sx={{
        width: 7, flexShrink: 0,
        borderLeft: 1, borderRight: 1, borderColor: 'divider',
        bgcolor: 'background.paper',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'col-resize',
        '&:hover': { bgcolor: 'action.hover' },
        '& *': { cursor: 'col-resize' },
      }}
    >
      <MoreVertIcon sx={{ fontSize: ICON_LG, opacity: 0.35 }} />
    </Box>
  )
}
